import { useEffect, useMemo, useRef, useState } from 'react'
import type { MessageEvent } from '../../../lib/contract'
import { usePrefersReducedMotion, useReveal } from '../../../lib/useReveal'
import styles from './Thread.module.css'

/**
 * THE THREAD — the whole surface, and deliberately almost nothing.
 *
 * Two sides and a scroll. What is *absent* here is the design, so each absence is named rather
 * than left to look like an oversight:
 *
 *  - **NO TIMESTAMPS.** No-visible-time is a display law (sæluhús content contract §25). The
 *    engine stamps every line because the parser and the 1:1 pacer read them, and the glass
 *    never voices one. A clock on the thread would turn "he hasn't answered" into a measurable
 *    wait, which is the opposite of what a hill that doesn't count days is for.
 *  - **NO READ RECEIPTS, NO TYPING INDICATOR, NO LAST-SEEN, NO UNREAD COUNT.** Every one of
 *    those is an accumulation mechanic. This surface borrows the clothes of the most
 *    engagement-optimised form factor ever built and must be loud about which parts it refuses
 *    (`2026-08-19-the-many-channels-format.md` §2.3, §4). The rule is: state, never score.
 *  - **NO AVATAR, NO NAME HEADER.** Glimpsed-never-rendered covers the whole world here — the
 *    reader builds him, and a picture would settle what should stay theirs.
 *
 * The one thing that IS shown while waiting is a resting mark, because a thread with no
 * acknowledgement at all reads as broken rather than unhurried.
 *
 * ── THE CORRESPONDENCE (2026-08-22) ───────────────────────────────────────────────────────
 * Under a theme this is no longer two columns of bubbles but ONE RUNNING SHEET on wood —
 * renamed from "the fire" in AESTHETIC because the guest is never in the room. The markup is
 * unchanged; the sheet is a wrapper the CSS paints, so an unthemed pack still gets bubbles.
 */
export default function Thread({
  messages,
  awaiting,
  paper = false,
  placedReveal = false,
}: {
  messages: MessageEvent[]
  awaiting: boolean
  /** Render as one sheet of paper on wood rather than as bubbles. */
  paper?: boolean
  /** His lines arrive word by word. Flagged — see the surface's `placedReveal`. */
  placedReveal?: boolean
}) {
  const endRef = useRef<HTMLDivElement | null>(null)

  /*
   * ONLY LINES THAT ARRIVE WHILE YOU ARE WATCHING MAY REVEAL.
   *
   * `useReveal` is mount-only, and on a resumed session the entire history mounts in one frame
   * — which without this would fade in every line the guest has ever received, at once, as
   * though the whole correspondence were being said to them again. The thread already on screen
   * when this component mounts is therefore banked as "already read" and rendered whole.
   */
  const alreadyThere = useRef<Set<string> | null>(null)
  if (alreadyThere.current === null) {
    alreadyThere.current = new Set(messages.map((m) => m.messageId))
  }

  // Follow the arriving edge. Lines land one at a time at speech tempo, so this
  // runs per line rather than per batch.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, awaiting])

  const lines = (
    <>
      {messages.map((m) => {
        // The player's own lines are the only ones this side sends. Everything
        // else on the thread is his — the server decides which is which, and
        // the client never infers it (dumb-client law 2).
        const mine = m.senderKind === 'Player'
        return (
          <p
            key={m.messageId}
            className={mine ? styles.mine : styles.theirs}
            data-register={m.registerClass}
          >
            {/* His lines only, and only ones that arrived just now. Your own words never
                reveal: you wrote them, and watching them appear a word at a time would be
                the surface performing at you. */}
            {!mine && placedReveal && !alreadyThere.current!.has(m.messageId) ? (
              <Placed text={m.content} />
            ) : (
              m.content
            )}
          </p>
        )
      })}

      {awaiting && (
        <p className={styles.resting} aria-label="waiting for a reply">
          <span />
          <span />
          <span />
        </p>
      )}
    </>
  )

  return (
    <div
      className={paper ? `${styles.thread} ${styles.onWood}` : styles.thread}
      role="log"
      aria-live="polite"
    >
      {paper ? (
        // The sheet is the scroll container's content and grows with the thread; the wood is
        // the surface behind it. One sheet, never one per line — a page of correspondence,
        // not a stack of cards.
        <div className={styles.sheet}>
          <div className={styles.lines}>{lines}</div>
        </div>
      ) : (
        <div className={styles.lines}>{lines}</div>
      )}
      <div ref={endRef} />
    </div>
  )
}

/**
 * THE PLACED REVEAL — one of his lines, a word at a time.
 *
 * The spec's own note is worth keeping at the code: this is the single strongest "not a chat"
 * signal on the surface, and the one piece with real UX risk. A line that cannot be skimmed
 * makes reading time follow *importance* rather than length — which is the point — but it also
 * takes control away from a fast reader, so:
 *
 *  - **A tap completes it**, immediately and whole. Nobody is ever made to wait.
 *  - **`prefers-reduced-motion` disables it** and the text is whole from the first frame.
 *  - ⚠ **THE REVEAL IS PACING, NEVER INFORMATION.** The full string is in the DOM from the
 *    first frame — a screen reader reads all of it at once, the words merely fade in for
 *    someone looking. Nothing may ever depend on the animation having run.
 */
function Placed({ text }: { text: string }) {
  const reduced = usePrefersReducedMotion()
  const words = useMemo(() => text.split(/\s+/).filter(Boolean).length, [text])
  const [done, setDone] = useState(false)

  // ~150ms a word, capped at four seconds — the opening run's pacing exactly, because a reader
  // meets the plain card first and the two would otherwise teach different tempos.
  const revealMs = reduced || done ? 0 : Math.min(4000, words * 150)
  const upto = useReveal(text, revealMs).length

  return (
    <span onClick={() => setDone(true)}>
      {revealMs === 0
        ? text
        : (() => {
            let at = 0
            // Whitespace rides with the word that follows it, so a space never fades on its
            // own and the gaps between words are stable from the first frame.
            return text.split(/(\S+\s*)/g).filter(Boolean).map((w, i) => {
              const start = at
              at += w.length
              return (
                <span key={i} className={styles.word} style={{ opacity: start < upto ? 1 : 0 }}>
                  {w}
                </span>
              )
            })
          })()}
    </span>
  )
}
